import BaseTooltip from 'components/Tooltips/BaseTooltip';
import { usePerms } from 'contexts/PermsContext';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { FaPencil } from 'react-icons/fa6';
import { useGetWorkplaceMembersQuery } from 'services/organization/organization.api';
import { getVisibleUsers } from 'utils/users/role.utils';
import { notifyErrorToast, notifySuccessToast } from 'utils/notifications/toast.utils';
import { usePersonnel } from '../../contexts/PersonnelContext';
import { usePersonnelMutations } from '../../hooks/usePersonnelMutations';
import Modal from './components/Modal';
import Table from './components/Table';
import {
  extractPersonnelDutyIds,
  extractPersonnelIds,
  getPersonnelSelectionDiff,
  stringifyId,
} from './utils/personnelListUtils';

/**
 * Renders the personnel roster and edit modal for adding/removing personnel assignments.
 * @param {{onSelectPersonnel?: (person: any) => void}} props
 * @returns {JSX.Element}
 */
export const PersonnelList = ({ onSelectPersonnel = null }) => {
  const perms = usePerms();
  const [showModal, setShowModal] = useState(false);
  const [fetchingMembers, setFetchingMembers] = useState(false);
  const [savingPersonnel, setSavingPersonnel] = useState(false);
  const [fusionMembers, setFusionMembers] = useState([]);
  const [selectedPersonnelIds, setSelectedPersonnelIds] = useState([]);
  const [membersFetchError, setMembersFetchError] = useState('');
  const [savePersonnelError, setSavePersonnelError] = useState('');
  const [hasFetchedMembers, setHasFetchedMembers] = useState(false);
  const membersFetchInFlightRef = useRef(false);
  const { personnel, fetchPersonnel, hasLoadedPersonnel, isPersonnelLoading, lastUpdateTime } =
    usePersonnel();
  const { createPersonnelForUserIds, deletePersonnelForUserIds, setPersonnelDuties } =
    usePersonnelMutations();
  const hasPersonnel = Object.keys(personnel).length > 0;
  const shouldShowPersonnelLoading = !hasPersonnel && (isPersonnelLoading || !hasLoadedPersonnel);

  const visibleAccessLevels = useMemo(() => getVisibleUsers(perms), [perms]);
  const { data: workplaceMembers = [] } = useGetWorkplaceMembersQuery(visibleAccessLevels);

  const personnelDutyIdByUserId = useMemo(() => {
    const dutyMap = {};
    Object.values(personnel || {}).forEach((person) => {
      const userId = person?.userId ?? person?.id;
      if (userId == null) return;
      const dutyIds = extractPersonnelDutyIds(person).map(stringifyId);
      dutyMap[stringifyId(userId)] = dutyIds;
    });
    return dutyMap;
  }, [personnel]);

  /**
   * Hydrates modal members from organization query data.
   * Guarded to prevent concurrent refresh calls while in-flight.
   * @returns {Promise<void>}
   */
  const handleFetchAllFusionMembers = useCallback(async () => {
    if (membersFetchInFlightRef.current) return;
    membersFetchInFlightRef.current = true;
    setFetchingMembers(true);
    setMembersFetchError('');
    try {
      const members = Array.isArray(workplaceMembers) ? workplaceMembers : [];
      setFusionMembers(members);
      setHasFetchedMembers(true);
    } catch (error) {
      setMembersFetchError('Failed to load members.');
      setHasFetchedMembers(false);
      console.error('[PersonnelList] /users/members request failed', error);
    } finally {
      membersFetchInFlightRef.current = false;
      setFetchingMembers(false);
    }
  }, [workplaceMembers]);

  useEffect(() => {
    setHasFetchedMembers(false);
  }, [visibleAccessLevels]);

  useEffect(() => {
    if (!showModal || hasFetchedMembers) return;
    handleFetchAllFusionMembers();
  }, [showModal, hasFetchedMembers, handleFetchAllFusionMembers]);

  useEffect(() => {
    if (showModal) return;
    setSelectedPersonnelIds(extractPersonnelIds(personnel));
  }, [personnel, showModal]);

  /**
   * Persists add/remove changes from modal selection to responder personnel.
   * @param {Array<string|number>} personnelIds
   * @returns {Promise<boolean>}
   */
  const handleSavePersonnelSelections = useCallback(
    async (personnelIds) => {
      if (savingPersonnel) return false;
      const { draftSelection, idsToAdd, idsToDelete } = getPersonnelSelectionDiff(
        selectedPersonnelIds,
        personnelIds,
      );
      if (!idsToAdd.length && !idsToDelete.length) return true;

      setSavePersonnelError('');
      setSavingPersonnel(true);
      try {
        if (idsToAdd.length) {
          await createPersonnelForUserIds(idsToAdd);
          notifySuccessToast('Personnel', { isAddOperation: true });
        }
        if (idsToDelete.length) {
          await deletePersonnelForUserIds(idsToDelete);
          notifySuccessToast('Personnel', { isAddOperation: false, isDeleteOperation: true });
        }
        setSelectedPersonnelIds(draftSelection.map(stringifyId));
        await fetchPersonnel?.();
        return true;
      } catch (error) {
        setSavePersonnelError('Failed to save personnel changes.');
        console.error('[PersonnelList] Failed to save selected personnel', error);
        notifyErrorToast(
          'Personnel',
          {
            isAddOperation: idsToAdd.length > 0,
            isDeleteOperation: idsToDelete.length > 0,
          },
          {
            errorPayload: error,
          },
        );
        return false;
      } finally {
        setSavingPersonnel(false);
      }
    },
    [
      createPersonnelForUserIds,
      deletePersonnelForUserIds,
      fetchPersonnel,
      savingPersonnel,
      selectedPersonnelIds,
    ],
  );

  const handleOpenModal = useCallback(() => {
    setSavePersonnelError('');
    setShowModal(true);
  }, []);

  /**
   * Updates duty assignment(s) for a single personnel record.
   * @param {string|number} userId
   * @param {Array<string|number>|string|number} dutyIds
   * @param {boolean|undefined|null} isSupervisor
   * @returns {Promise<boolean>}
   */
  const handleSelectPersonnelDuty = useCallback(
    async (userId, dutyIds, isSupervisor) => {
      if (userId == null || userId === '') return false;

      setSavePersonnelError('');
      try {
        await setPersonnelDuties(userId, dutyIds, isSupervisor);
        await fetchPersonnel?.();
        notifySuccessToast('Personnel duty', { isAddOperation: false });
        return true;
      } catch (error) {
        setSavePersonnelError('Failed to update personnel duty.');
        console.error('[PersonnelList] Failed to update personnel duty', error);
        notifyErrorToast('Personnel duty', { isAddOperation: false }, { errorPayload: error });
        return false;
      }
    },
    [fetchPersonnel, setPersonnelDuties],
  );

  const handleRefreshPersonnel = useCallback(() => {
    fetchPersonnel();
  }, [fetchPersonnel]);

  return (
    <div
      className="p-3 rounded-2 h-100 d-flex flex-column bg-primary-shadow text-theme"
      style={{
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      <div className="d-flex w-100">
        <h4>Personnel</h4>
        <BaseTooltip
          id="personnel-list-create"
          text="Edit Personnel"
          trigger={['hover']}
          blurOnClick
        >
          <Button
            className="ms-auto btn-transparent"
            type="button"
            onClick={handleOpenModal}
            disabled={fetchingMembers}
          >
            <FaPencil />
          </Button>
        </BaseTooltip>
      </div>
      <div style={{ cursor: 'pointer' }} onClick={handleRefreshPersonnel}>
        <div className="text-theme-muted" style={{ fontSize: '0.8em', marginBottom: '10px' }}>
          {lastUpdateTime ? `Last updated: ${lastUpdateTime.toLocaleTimeString()} ⟳` : 'Loading...'}
        </div>
      </div>
      <div className="flex-grow-1" style={{ minHeight: 0 }}>
        {hasPersonnel ? (
          <Table
            personnel={personnel}
            onSelectPersonnel={onSelectPersonnel}
            personnelDutyIdByUserId={personnelDutyIdByUserId}
            onSelectPersonnelDuty={handleSelectPersonnelDuty}
          />
        ) : shouldShowPersonnelLoading ? (
          <div
            className="d-flex align-items-center gap-2 text-theme-muted"
            role="status"
            aria-live="polite"
          >
            <Spinner animation="border" size="sm" />
            <span>Loading personnel...</span>
          </div>
        ) : (
          <p className="text-theme-muted">No personnel available.</p>
        )}
      </div>
      <Modal
        show={showModal}
        handleShow={() => setShowModal(false)}
        members={fusionMembers}
        loading={fetchingMembers}
        saving={savingPersonnel}
        error={savePersonnelError || membersFetchError}
        onRefresh={handleFetchAllFusionMembers}
        selectedPersonnelIds={selectedPersonnelIds}
        onSavePersonnel={handleSavePersonnelSelections}
      />
    </div>
  );
};
